import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import API_URL from './config';
import './WaiverPage.css';

function WaiverPage() {
  const navigate = useNavigate();
  const [waiverText, setWaiverText] = useState('');
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');

  const [formData, setFormData] = useState({
    signerFirstName: '',
    signerLastName: '',
    minorFirstName: '',
    minorLastName: '',
    address: '',
    city: '',
    state: '',
    zip: '',
    email: '',
    phone: '',
    consentGranted: false,
    signature: ''
  });

  useEffect(() => {
    // Load waiver text from public folder
    fetch(process.env.PUBLIC_URL + '/waiver.txt')
      .then(res => res.text())
      .then(text => {
        setWaiverText(text);
        setLoading(false);
      })
      .catch(err => {
        setWaiverText('Unable to load waiver text. Please try again later.');
        setLoading(false);
      });
  }, []);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    // Validate required fields
    if (!formData.signerFirstName || !formData.signerLastName) {
      setError('Please enter the signer\'s first and last name.');
      return;
    }
    if (!formData.minorFirstName || !formData.minorLastName) {
      setError('Please enter the minor participant\'s first and last name.');
      return;
    }
    if (!formData.address || !formData.city || !formData.state || !formData.zip) {
      setError('Please enter a complete address.');
      return;
    }
    if (!formData.email) {
      setError('Please enter an email address.');
      return;
    }
    if (!formData.phone) {
      setError('Please enter a phone number.');
      return;
    }
    if (!formData.consentGranted) {
      setError('You must grant consent to continue.');
      return;
    }
    if (!formData.signature) {
      setError('Please type your name as your legal signature.');
      return;
    }

    setSubmitting(true);

    // Send waiver to backend to generate PDF and email
    try {
      const response = await fetch(`${API_URL}/api/waiver/submit`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData)
      });
      
      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.error || 'Failed to submit waiver');
      }
      
      setSubmitted(true);
    } catch (err) {
      setError(err.message || 'Failed to submit waiver. Please try again.');
    }
    setSubmitting(false);
  };

  if (submitted) {
    return (
      <div className="waiver-page">
        <div className="waiver-container">
          <div className="waiver-success">
            <h2>Waiver Submitted Successfully</h2>
            <p>Thank you for completing the participant waiver.</p>
            <button className="waiver-btn" onClick={() => navigate('/')}>
              Return to Home
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="waiver-page">
      <div className="waiver-container">
        <h1 className="waiver-title">Participant Waiver</h1>
        
        <div className="waiver-text-box">
          {loading ? (
            <p>Loading waiver...</p>
          ) : (
            <pre className="waiver-text">{waiverText}</pre>
          )}
        </div>

        <form className="waiver-form" onSubmit={handleSubmit}>
          <h2>Waiver Agreement Form</h2>
          
          {error && <div className="waiver-error">{error}</div>}

          <div className="waiver-section">
            <h3>Signer Information</h3>
            <div className="waiver-row">
              <div className="waiver-field">
                <label>First Name *</label>
                <input
                  type="text"
                  name="signerFirstName"
                  value={formData.signerFirstName}
                  onChange={handleChange}
                  required
                />
              </div>
              <div className="waiver-field">
                <label>Last Name *</label>
                <input
                  type="text"
                  name="signerLastName"
                  value={formData.signerLastName}
                  onChange={handleChange}
                  required
                />
              </div>
            </div>
          </div>

          <div className="waiver-section">
            <h3>Minor Participant Information</h3>
            <div className="waiver-row">
              <div className="waiver-field">
                <label>First Name *</label>
                <input
                  type="text"
                  name="minorFirstName"
                  value={formData.minorFirstName}
                  onChange={handleChange}
                  required
                />
              </div>
              <div className="waiver-field">
                <label>Last Name *</label>
                <input
                  type="text"
                  name="minorLastName"
                  value={formData.minorLastName}
                  onChange={handleChange}
                  required
                />
              </div>
            </div>
          </div>

          <div className="waiver-section">
            <h3>Address</h3>
            <div className="waiver-field full-width">
              <label>Street Address *</label>
              <input
                type="text"
                name="address"
                value={formData.address}
                onChange={handleChange}
                required
              />
            </div>
            <div className="waiver-row">
              <div className="waiver-field">
                <label>City *</label>
                <input
                  type="text"
                  name="city"
                  value={formData.city}
                  onChange={handleChange}
                  required
                />
              </div>
              <div className="waiver-field small">
                <label>State *</label>
                <input
                  type="text"
                  name="state"
                  value={formData.state}
                  onChange={handleChange}
                  maxLength={2}
                  required
                />
              </div>
              <div className="waiver-field small">
                <label>Zip *</label>
                <input
                  type="text"
                  name="zip"
                  value={formData.zip}
                  onChange={handleChange}
                  required
                />
              </div>
            </div>
          </div>

          <div className="waiver-section">
            <h3>Contact Information</h3>
            <div className="waiver-row">
              <div className="waiver-field">
                <label>Email *</label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                />
              </div>
              <div className="waiver-field">
                <label>Phone *</label>
                <input
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  required
                />
              </div>
            </div>
          </div>

          <div className="waiver-section consent-section">
            <label className="waiver-checkbox-label">
              <input
                type="checkbox"
                name="consentGranted"
                checked={formData.consentGranted}
                onChange={handleChange}
                required
              />
              <span>I grant Granger Sports Complex consent to collect general information, including name, phone number, address, and email address.</span>
            </label>
          </div>

          <div className="waiver-section signature-section">
            <h3>Legal Signature</h3>
            <p className="signature-note">By typing your name below, you acknowledge that this constitutes your legal signature.</p>
            <div className="waiver-field full-width">
              <label>Type Your Full Name *</label>
              <input
                type="text"
                name="signature"
                value={formData.signature}
                onChange={handleChange}
                className="signature-input"
                placeholder="Type your full legal name"
                required
              />
            </div>
          </div>

          <div className="waiver-actions">
            <button type="button" className="waiver-btn secondary" onClick={() => navigate('/')}>
              Cancel
            </button>
            <button type="submit" className="waiver-btn primary" disabled={submitting}>
              {submitting ? 'Submitting...' : 'Submit Waiver'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default WaiverPage;
